/**
 * GenerateScheduleDialog — Preview and apply auto-generated schedule
 */

import { useState, useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import { nl } from 'date-fns/locale';
import type { GenerateResult } from '../../lib/auto-schedule';

const shiftTypeColors: Record<string, string> = {
  wasserij: 'text-blue-700 bg-blue-50',
  route: 'text-emerald-700 bg-emerald-50',
  kantoor: 'text-purple-700 bg-purple-50',
};

interface GenerateScheduleDialogProps {
  result: GenerateResult;
  weekLabel: string;
  onApply: () => Promise<void>;
  onClose: () => void;
}

export function GenerateScheduleDialog({ result, weekLabel, onApply, onClose }: GenerateScheduleDialogProps) {
  const [isApplying, setIsApplying] = useState(false);

  // Group shifts by staff
  const staffGroups = useMemo(() => {
    const groups = new Map<string, { name: string; shifts: typeof result.shifts }>();
    for (const shift of result.shifts) {
      if (!groups.has(shift.staff_id)) {
        groups.set(shift.staff_id, { name: shift.staff_name, shifts: [] });
      }
      groups.get(shift.staff_id)!.shifts.push(shift);
    }
    return Array.from(groups.entries()).sort((a, b) => a[1].name.localeCompare(b[1].name));
  }, [result.shifts]);

  const handleApply = async () => {
    setIsApplying(true);
    await onApply();
    setIsApplying(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100">
          <h3 className="text-lg font-semibold text-main">Rooster Genereren</h3>
          <p className="text-sm text-muted mt-1">{weekLabel}</p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {result.shifts.length === 0 ? (
            <p className="text-sm text-muted py-4 text-center">
              Geen diensten te genereren. Alle slots zijn bezet, of medewerkers hebben geen werkrooster.
            </p>
          ) : (
            <>
              <p className="text-xs text-muted mb-4">
                Concept-rooster op basis van standaard werkroosters:
              </p>

              <div className="space-y-4">
                {staffGroups.map(([staffId, group]) => (
                  <div key={staffId}>
                    <p className="text-sm font-medium text-main mb-1.5">
                      {group.name}
                      <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${shiftTypeColors[group.shifts[0]?.shift_type || ''] || ''}`}>
                        {group.shifts[0]?.shift_type}
                      </span>
                    </p>
                    <div className="space-y-1">
                      {group.shifts.map(shift => (
                        <div key={`${shift.staff_id}-${shift.date}`} className="flex items-center gap-2 text-sm pl-2">
                          <span className="w-4 h-4 rounded bg-emerald-100 text-emerald-600 flex items-center justify-center text-[10px]">
                            ✓
                          </span>
                          <span className="text-muted w-16">
                            {format(parseISO(shift.date), 'EEE', { locale: nl })}
                          </span>
                          <span className="text-main">{shift.time_from}–{shift.time_to}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Skipped summary */}
              {result.skipped.length > 0 && (
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <p className="text-xs text-muted mb-2">
                    Overgeslagen: {result.skipped.length} ({[...new Set(result.skipped.map(s => s.reason))].join(', ')})
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 text-sm font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
          >
            Annuleren
          </button>
          <button
            onClick={handleApply}
            disabled={isApplying || result.shifts.length === 0}
            className="flex-1 py-2.5 text-sm font-medium text-white bg-indigo-500 rounded-lg hover:bg-indigo-600 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
          >
            {isApplying && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
            {isApplying ? 'Genereren...' : `Genereer ${result.shifts.length} diensten`}
          </button>
        </div>
      </div>
    </div>
  );
}
